import { isUnresolvedCategory } from '@folio/utils/document-categories';
import { useMemo } from 'react';

type ConformityDecision = 'PENDING' | 'ACCEPTED' | 'REJECTED';

const SYSTEM_USER_ID = 'system';

interface ConformityDto {
  documentId: string;
  id: string;
  status: string;
  decision: ConformityDecision;
  reason?: string;
  decidedByUserId: string;
}

interface DocumentWithCategory {
  id: string;
  type: 'generated' | 'supporting_document' | 'non_delegated' | null;
  category: string | null;
}

/** Builds the two lookup maps every conformity-aware surface needs from a document list
 * and a conformity query result: current decision per document, and active conformity id
 * per document (for cancelling). Shared across policy/brokerage-firm/policyholder surfaces
 * since the derivation is identical regardless of entity type. */
export function useDocumentConformityMap(
  documents: DocumentWithCategory[] | undefined,
  conformities: ConformityDto[] | undefined,
  isDocumentReviewEnabled: boolean,
) {
  const conformityByDocumentId = useMemo(() => {
    const map = new Map<
      string,
      {
        decision: ConformityDecision;
        reason?: string;
        decidedAutomatically?: boolean;
      }
    >();
    if (!isDocumentReviewEnabled) return map;
    for (const document of documents ?? []) {
      if (document.type !== 'supporting_document' || isUnresolvedCategory(document.category))
        continue;
      map.set(document.id, { decision: 'PENDING' });
    }
    for (const conformity of conformities ?? []) {
      if (conformity.status !== 'ACTIVE') continue;
      map.set(conformity.documentId, {
        decision: conformity.decision,
        reason: conformity.reason,
        decidedAutomatically: conformity.decidedByUserId === SYSTEM_USER_ID,
      });
    }
    return map;
  }, [isDocumentReviewEnabled, documents, conformities]);

  const conformityIdByDocumentId = useMemo(() => {
    const map = new Map<string, string>();
    for (const conformity of conformities ?? []) {
      if (conformity.status === 'ACTIVE') map.set(conformity.documentId, conformity.id);
    }
    return map;
  }, [conformities]);

  return { conformityByDocumentId, conformityIdByDocumentId };
}
